/**
 * 港前报价管理前端接口
 */

import { request } from '@/utils/http'
import { type BatchDeleteRequest, type PageResponse } from '@/api/types/common'
import {
  type InitiateConsultationRequest, type InqQuotPriceChargingItemVO,
  type QuotationBaseModel, type QuotationChargingItemVO,
  type QuotationPageRequest,
  type QuotationSaveRequest
} from './quotation-base'

/**
 * 港前报价模型（继承基础报价模型）
 */
export interface QuotationPrePortModel extends QuotationBaseModel {
}

/**
 * 港前报价分页查询请求参数
 */
export interface QuotationPrePortPageRequest extends QuotationPageRequest {
}

/**
 * 港前报价保存请求参数
 */
export interface QuotationPrePortSaveRequest extends QuotationSaveRequest {
}

/**
 * 分页查询港前报价列表
 */
export const pageQuotationPrePort = (params: QuotationPrePortPageRequest) => {
  return request.post<PageResponse<QuotationPrePortModel>>('/price/quotation/pre-port/page', params)
}

/**
 * 根据ID获取港前报价详情
 */
export const getQuotationPrePortById = (id: string) => {
  return request.post<QuotationPrePortModel>('/price/quotation/pre-port/get', { id })
}

/**
 * 根据询价编号获取港前报价详情
 */
export const getQuotationPrePortByInquiryNo = (inquiryNo: string) => {
  return request.post<QuotationPrePortModel>('/price/quotation/pre-port/get', { inquiryNo })
}

/**
 * 新增港前报价
 */
export const saveQuotationPrePort = (params: QuotationPrePortSaveRequest) => {
  return request.post<boolean>('/price/quotation/pre-port/save', params)
}

/**
 * 修改港前报价
 */
export const updateQuotationPrePort = (params: QuotationPrePortSaveRequest) => {
  return request.post<boolean>('/price/quotation/pre-port/update', params)
}

/**
 * 批量删除港前报价
 */
export const batchDeleteQuotationPrePort = (params: BatchDeleteRequest) => {
  return request.post<boolean>('/price/quotation/pre-port/batch-delete', params)
}

/**
 * 发起新磋商
 * 针对指定的整箱报价单发起新的磋商记录,更新报价单磋商状态为"磋商中"
 */
export const initiateConsultationPrePort = (params: InitiateConsultationRequest) => {
  return request.post<boolean>('/price/quotation/pre-port/initiateConsultation', params)
}

/**
 * 商户报价单加价保存
 */
export const businessQuotationMarkupPrePort = (params: InqQuotPriceChargingItemVO) => {
  return request.post<InqQuotPriceChargingItemVO>('/price/quotation/pre-port/businessQuotationMarkup', params)
}

/**
 * 供应商报价单加价保存
 */
export const supplierQuotationMarkupPrePort = (params: QuotationChargingItemVO) => {
  return request.post<QuotationChargingItemVO>('/price/quotation/pre-port/supplierQuotationMarkup', params)
}

// ---- 匿名报价接口 ----

export interface LinkAccess {
  token: string
}

/** 附件信息 */
export interface BusinessFileVO {
  id?: string
  name?: string
  fileName?: string
  url?: string
  size?: number
  [key: string]: unknown
}

/** 港前询价货物（InquiryPrePortCargoVO） */
export interface InquiryPrePortCargoVO {
  id?: string
  inquiryId?: string
  /** 箱型（FCL） */
  containerType?: string
  /** 箱量（FCL） */
  containerNum?: number
  /** 件数（LCL/TRUCK） */
  quantity?: number
  /** 毛重 */
  weight?: number
  length?: number
  width?: number
  height?: number
  /** 包装方式（LCL） */
  packageId?: number
  /** 车型（TRUCK）：1=敞车 2=棚车 3=平车 4=冷藏车 5=矿石车 6=水泥车 */
  truckType?: number
  /** 温度范围-起始（冷冻货） */
  temperatureStart?: number
  /** 温度范围-结束（冷冻货） */
  temperatureEnd?: number
  /** 通风开关（冷冻货） */
  ventilationEnable?: boolean
  /** 通风量（冷冻货） */
  ventilationQuantity?: number
  /** 湿度（冷冻货） */
  humidity?: number
  /** 是否预冷（冷冻货） */
  needPrecooling?: boolean
  remarks?: string
  [key: string]: unknown
}

/** 港前询价基础信息（InquiryPrePortListVO + InquiryBaseVO 关键字段） */
export interface InquiryPrePortListVO {
  id?: string
  inquiryNo?: string
  status?: number
  /** 服务类型：FCL/LCL/TRUCK */
  serviceType?: string
  /** 运输方式：1=拖车 2=驳船 3=铁路 */
  transportMode?: number
  /** 配送地址类型（到达节点类型）：1=海港 2=铁路站点 3=机场 */
  arrivalNodeType?: number
  /** 询价性质：1=询价 2=实单 */
  palletNature?: number
  /** 付款方式 */
  paymentMethod?: string
  /** 贸易条款 */
  serviceTerm?: string
  /** 运输条款 */
  transportClause?: string
  cargoReadyTimeType?: string
  cargoReadyTimeRange?: number
  cargoReadyTimeDate?: string
  etd?: string
  cargoType?: number
  unNo?: number
  hazardClass?: number
  cargoDescription?: string
  hsCode?: string
  cargoValue?: number
  cargoValueCurrency?: string
  quotationDeadline?: string
  specialRequirements?: string
  onePriceSupported?: boolean
  // ---- InquiryBaseVO 字段 ----
  stuffingCountryId?: string
  stuffingProvinceId?: string
  stuffingCityId?: string
  stuffingDistrictId?: string
  stuffingStreetId?: string
  stuffingVillageId?: string
  stuffingDetailAddress?: string
  postalCode?: string
  /** 装货城市（驳船） */
  pickupCityId?: string
  /** 装货港（驳船） */
  pickupPortId?: string
  /** 装货站（铁路） */
  pickupRailwayStationId?: string
  loadingPortId?: string
  stuffingTerminalId?: string
  carrierId?: string
  warehouseId?: string
  loadingCountryId?: string
  loadingCityId?: string
  loadingRailwayStationId?: string
  loadingAirportId?: string
  airportTerminal?: string
  cargoList?: InquiryPrePortCargoVO[]
  [key: string]: unknown
}

/** 港前报价费用条目（QuotationPrePortChargingItemVO） */
export interface QuotationPrePortChargingItemVO {
  id?: string
  quotationId?: string
  chargeItemId?: string | number
  /** 费用类型：0=基础费用 1=附加费 2=潜在费用（无 feeMainType！） */
  feeType?: number
  /** 计费单位类型：1=按箱 2=非按箱 */
  billingUnitType?: number
  /** 箱型价格 JSON（按箱时使用）：{ "20GP": 150, "40HQ": 260 } */
  eqPrice?: Record<string, number> | string
  /** 箱型卖价 JSON（询价人模式取此槽） */
  eqPriceSell?: Record<string, number> | string
  /** 单价（非按箱时使用） */
  unitRate?: number | string
  /** 单价卖价（询价人模式取此槽） */
  unitRateSell?: number | string
  /** 总价是否已包含 */
  isTotalIncluded?: boolean
  /** 是否计入总价 */
  isCountTotal?: boolean
  /** 最低收费 */
  minCharge?: number | string
  /** 最高收费 */
  maxCharge?: number | string
  /** 最低收费卖价 */
  minChargeSell?: number | string
  /** 最高收费卖价 */
  maxChargeSell?: number | string
  /** 总价 */
  totalPrice?: number | string
  /** 总价卖价 */
  totalPriceSell?: number | string
  /** 委托链路共享费用行标识（销售/供应商两侧同值） */
  linkId?: string
  measureId?: string
  currency?: string
  /** 支付方式：1=预付 2=到付 */
  paymentType?: number
  remarks?: string
  [key: string]: unknown
}

/** 港前报价方案（QuotationPrePortListVO + QuotationBaseVO 关键字段） */
export interface QuotationPrePortListVO {
  id?: string
  quotationNo?: string
  status?: number
  inquiryId?: string
  inquiryNo?: string
  supplierId?: string
  supplierName?: string
  contactPhone?: string
  contactEmail?: string
  quoterId?: string
  consultationStatus?: number
  remarks?: string
  supplierRemarks?: string
  onePrice?: boolean
  onePriceSupported?: boolean
  validFrom?: string
  validTo?: string
  /** 运输方式：1=拖车 2=驳船 3=铁路 */
  transportMode?: number
  /** 装货港（驳船） */
  pickupPortId?: string
  /** 装货站（铁路） */
  pickupRailwayStationId?: string
  /** 船公司/驳船承运人 */
  carrierId?: string
  /** 驳船船名 */
  vessel?: string
  /** 驳船航次 */
  voyage?: string
  /** 驳船开航时间 */
  bargeEtd?: string
  /** 驳船到港时间 */
  bargeEta?: string
  /** 铁路公司 */
  railCompanyId?: string
  /** 线路分类 */
  railLineType?: string
  /** 车次 */
  railTrainNo?: string
  /** 发车时间 */
  railDepartureTime?: string
  /** 配送地址类型（到达节点类型）：1=海港 2=铁路站点 3=机场 */
  arrivalNodeType?: number
  /** 起运国 */
  loadingCountryId?: string
  /** 起运城市 */
  loadingCityId?: string
  /** 起运港 */
  loadingPortId?: string
  /** 起运码头 */
  stuffingTerminalId?: string
  /** 起运站（铁路） */
  loadingRailwayStationId?: string
  /** 起运机场 */
  loadingAirportId?: string
  /** 机场货站 */
  airportTerminal?: string
  /** 运输天数 */
  transportationDays?: number
  chargingItemList?: QuotationPrePortChargingItemVO[]
  files?: BusinessFileVO[]
  [key: string]: unknown
}

/** 港前匿名报价详情（含询价信息） */
export interface QuotationPrePortAnonymousDetailVO {
  quotations?: QuotationPrePortListVO[]
  inquiry?: InquiryPrePortListVO
}

/**
 * 通过 token 查询邮件授权范围内的港前报价方案列表（含询价信息）
 * 后端：QuotationPrePortController#queryByInquiryAnonymous
 */
export const queryByInquiryPrePortAnonymous = (access: LinkAccess) =>
  request.post<QuotationPrePortAnonymousDetailVO>('/price/quotation/pre-port/anonymous/queryByInquiry', { token: access.token })

export const createAnonymousQuotationPrePortScheme = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<number>('/price/quotation/pre-port/anonymous/createScheme', { quotation, token: access.token })

export const updateAnonymousQuotationPrePort = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<boolean>('/price/quotation/pre-port/anonymous/update', { quotation, token: access.token })

export const batchSubmitAnonymousQuotationPrePort = (quotations: Record<string, unknown>[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/pre-port/anonymous/batchSubmit', { quotations, token: access.token })

export const batchDeleteAnonymousQuotationPrePort = (ids: (string | number)[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/pre-port/anonymous/batchDelete', { ids, token: access.token })
